import { useRef, useState } from "react";
import { UploadCloud } from "lucide-react";

// Reusable drop-zone. Wraps arbitrary content and shows a subtle highlighted
// overlay while a matching file is being dragged over it. Calls
// `onFiles(files[])` when the user drops. Options:
//  • `multiple` — pass all matching files (default: just the first)
//  • `accept`   — predicate (file) => bool. Defaults to image/* files.
//  • `label`    — customise the overlay hint.
export default function DropZone({ onFiles, multiple = false, accept, className = "", children, label, testId }) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const matches = accept || ((f) => f.type.startsWith("image/"));

  const hasFiles = (dt) => {
    if (!dt) return false;
    // In `dragenter`/`dragover`, `files` isn't populated but `types` includes
    // "Files" when a file is being dragged.
    if (dt.types && Array.from(dt.types).includes("Files")) return true;
    return dt.items && Array.from(dt.items).some((x) => x.kind === "file");
  };

  return (
    <div
      data-testid={testId}
      className={`relative ${className}`}
      onDragEnter={(e) => {
        if (!hasFiles(e.dataTransfer)) return;
        e.preventDefault();
        depth.current += 1;
        setOver(true);
      }}
      onDragOver={(e) => {
        if (!hasFiles(e.dataTransfer)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }}
      onDragLeave={(e) => {
        if (!hasFiles(e.dataTransfer)) return;
        depth.current -= 1;
        if (depth.current <= 0) {
          depth.current = 0;
          setOver(false);
        }
      }}
      onDrop={(e) => {
        if (!hasFiles(e.dataTransfer)) return;
        e.preventDefault();
        setOver(false);
        depth.current = 0;
        const all = Array.from(e.dataTransfer.files || []).filter(matches);
        if (!all.length) return;
        onFiles(multiple ? all : [all[0]]);
      }}
    >
      {children}
      {over && (
        <div className="absolute inset-0 z-30 pointer-events-none rounded-md border-2 border-dashed border-sky-500 bg-sky-50/85 dark:bg-sky-950/80 grid place-items-center">
          <div className="flex flex-col items-center gap-1 text-sky-800 dark:text-sky-200">
            <UploadCloud className="w-6 h-6" />
            <div className="text-sm font-semibold">{label || (multiple ? "Drop files to upload" : "Drop file to upload")}</div>
          </div>
        </div>
      )}
    </div>
  );
}
